import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  IconButton,
  useTheme,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { observer } from "mobx-react-lite";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";
import { useStores } from "../../../use-stores";
import { useNotification } from "../../../../../utils/NotificationsContext";
import MediaAssetsUpload from "../../../../../views/components/common/upload/MediaAssetsUpload";
import ImageAPI from "../../../api/ImageAPI";
import { UrgentSourcingRaisedRequestForm } from "../../../../../models/UrgentSourcing/UrgentSourcing";
import AppDropDown from "../../../../../views/components/common/filter/AppDropDown";
import { Urgent_sorcing_priority_list } from "../../../../../models/UrgentSourcing/UrgentSourcingPriorityList";
import FormSmallTextField from "../../../../../views/components/common/form/FormTextField";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
interface UrgentSourcingRaiseRequestPopUpProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: UrgentSourcingRaisedRequestForm) => void;
}

const UrgentSourcingRaiseRequestPopUp: React.FC<
  UrgentSourcingRaiseRequestPopUpProps
> = ({ open, onClose, onSave }) => {
  const { urgentSourcingStore } = useStores();
  const { showError } = useNotification();
  const theme = useTheme();

  const isFormValid = () => {
    const { leadId, companyName, products } =
      urgentSourcingStore.raisedRequestFormData;
    if (!leadId.trim() || !companyName.trim()) {
      return false;
    }

    const allProductsValid = products.every(
      (product) =>
        product.productName?.trim() &&
        product.priority >= 0 &&
        product.timeline &&
        product.timeline.toString() &&
        product.quantity &&
        product.quantity.toString() &&
        product.minPriceRange &&
        product.minPriceRange.toString() &&
        product.maxPriceRange &&
        (product.link?.trim() || product.imageUrls) &&
        product.description?.trim(),
    );

    return allProductsValid;
  };

  const handleSave = () => {
    if (isFormValid()) {
      urgentSourcingStore.setIsLoading(true);

      const productPromises =
        urgentSourcingStore.raisedRequestFormData.products.map((product) => {
          let linkValue = product.link;

          if (product.imageUrls && typeof product.imageUrls === "object") {
            const imageAPI = new ImageAPI();
            return imageAPI
              .uploadImage([product.imageUrls as File], "product-images")
              .then((uploadedUrls) => {
                linkValue = uploadedUrls[0];
                return {
                  ...product,
                  link: linkValue,
                };
              });
          }

          return Promise.resolve({
            ...product,
            link: linkValue,
          });
        });

      Promise.all(productPromises)
        .then((productsWithUploadedImages) => {
          const formDataToSave = {
            leadId: urgentSourcingStore.raisedRequestFormData.leadId,
            companyName: urgentSourcingStore.raisedRequestFormData.companyName,
            requestDate: urgentSourcingStore.raisedRequestFormData.requestDate,
            products: productsWithUploadedImages.map((product) => ({
              productName: product.productName,
              priority: product.priority,
              quantity: product.quantity,
              timeline: product.timeline,
              minPriceRange: product.minPriceRange,
              maxPriceRange: product.maxPriceRange,
              link: product.link,
              description: product.description,
            })),
          };

          onSave(formDataToSave);
        })
        .catch((error) => {
          urgentSourcingStore.setIsLoading(false);
          const errorMessage =
            error?.response?.data?.message ||
            error?.message ||
            "Failed to upload image. Please try again.";
          showError("Upload Error", errorMessage);
        });
    }
  };

  const handleReset = () => {
    urgentSourcingStore.resetRaisedReqForm();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      slotProps={{
        transition: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        backdrop: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
            maxHeight: "90vh",
            maxWidth: {
              xs: "95vw",
              sm: "90vw",
              md: "80vw",
              lg: "60vw",
              xl: "50vw",
            },
            width: {
              xs: "95vw",
              sm: "90vw",
              md: "80vw",
              lg: "60vw",
              xl: "50vw",
            },
            margin: { xs: "16px", sm: "32px" },
            background: `linear-gradient(135deg, ${theme.palette.customColors.white} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
          },
        },
      }}
      sx={{
        zIndex: 2000,
        "& .MuiDialog-container": {
          alignItems: "center",
          justifyContent: "center",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: { xs: "12px 16px 12px", sm: "16px 20px 16px" },
            borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
          }}
        >
          <CustomTypography variant="heading3">
            Raise Urgent Sourcing Request
          </CustomTypography>
          <IconButton
            onClick={onClose}
            sx={{
              color: theme.palette.customColors.greyTextColor,
              "&:hover": {
                backgroundColor: theme.palette.customColors.lightGreyColor,
                color: theme.palette.customColors.blackBorder,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            padding: { xs: "20px", sm: "24px" },
            flex: 1,
            overflow: "auto",
            maxHeight: "60vh",
            scrollBehavior: "auto",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.customColors.greyBackground,
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.customColors.greyDarkborder,
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: theme.palette.customColors.greyTextColor,
              },
            },
          }}
        >
          <Box sx={{ mb: 4 }}>
            <Box
              sx={{
                mb: 3,
                display: "flex",
                gap: 4,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Box sx={{ flex: 1 }}>
                <CustomTypography
                  variant="heading6"
                  style={{ fontWeight: 600, marginBottom: 16 }}
                >
                  Lead ID{" "}
                  <span style={{ color: theme.palette.error.main }}>*</span>
                </CustomTypography>

                <CustomTextField
                  width="100%"
                  placeholder="Enter lead ID"
                  value={urgentSourcingStore.raisedRequestFormData.leadId}
                  onChange={(e) =>
                    urgentSourcingStore.updateRaisedReqFormField(
                      "leadId",
                      e.target.value,
                    )
                  }
                />
              </Box>

              <Box sx={{ flex: 1 }}>
                <CustomTypography
                  variant="heading6"
                  style={{ fontWeight: 600, marginBottom: 16 }}
                >
                  Company Name{" "}
                  <span
                    style={{ color: theme.palette.error.main, marginLeft: 4 }}
                  >
                    *
                  </span>
                </CustomTypography>

                <CustomTextField
                  width="100%"
                  placeholder="Enter company name"
                  value={urgentSourcingStore.raisedRequestFormData.companyName}
                  onChange={(e) =>
                    urgentSourcingStore.updateRaisedReqFormField(
                      "companyName",
                      e.target.value,
                    )
                  }
                />
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 4 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <CustomTypography
                variant="heading5"
                style={{
                  fontWeight: 600,
                  color: theme.palette.customColors.blackBorder,
                }}
              >
                Products
              </CustomTypography>

              <AppButton
                label="Add Product"
                onClick={urgentSourcingStore.addProductSection}
                variant="primary"
              />
            </Box>

            {urgentSourcingStore.raisedRequestFormData.products.map(
              (product, productIndex) => {
                if (!product) return null;

                return (
                  <Accordion
                    key={productIndex}
                    defaultExpanded={true}
                    sx={{
                      border: `1px solid ${theme.palette.customColors.greyBorder}`,
                      borderRadius: "12px !important",
                      mb: 3,
                      backgroundColor: theme.palette.customColors.white,
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.12)",
                      },
                      "&:before": {
                        display: "none",
                      },
                      "&.Mui-expanded": {
                        margin: "0 0 24px 0",
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={
                        <ExpandMoreIcon
                          sx={{
                            color: theme.palette.customColors.blueGradientStart,
                          }}
                        />
                      }
                      sx={{
                        backgroundColor:
                          theme.palette.customColors.lightGreyColor,
                        borderRadius: "12px 12px 0 0",
                        minHeight: "48px",
                        "& .MuiAccordionSummary-content": {
                          margin: "8px 0",
                          alignItems: "center",
                          justifyContent: "space-between",
                        },
                        "& .MuiAccordionSummary-expandIconWrapper": {
                          color: theme.palette.customColors.blueGradientStart,
                        },
                        "&:hover": {
                          backgroundColor:
                            theme.palette.customColors.greyBackground,
                        },
                        "&:not(.Mui-disabled)": {
                          cursor: "default",
                        },
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <CustomTypography
                          variant="heading6"
                          style={{ fontWeight: 600 }}
                        >
                          Product {productIndex + 1}
                        </CustomTypography>
                      </Box>

                      {urgentSourcingStore.raisedRequestFormData.products
                        .length > 1 && (
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            urgentSourcingStore.removeProductSection(
                              productIndex,
                            );
                          }}
                          sx={{
                            color: theme.palette.error.main,

                            "&:hover": {
                              backgroundColor: theme.palette.error.main + "20",
                            },
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </AccordionSummary>

                    <AccordionDetails sx={{ padding: "24px" }}>
                      <Box
                        sx={{
                          mb: 3,
                          display: "flex",
                          gap: 4,
                          flexDirection: { xs: "column", sm: "row" },
                        }}
                      >
                        <Box sx={{ flex: 1, mb: 1 }}>
                          <CustomTypography
                            variant="heading6"
                            style={{ fontWeight: 600, marginBottom: 16 }}
                          >
                            Product Name{" "}
                            <span
                              style={{
                                color: theme.palette.error.main,
                                marginLeft: 4,
                              }}
                            >
                              *
                            </span>
                          </CustomTypography>
                          <CustomTextField
                            width="100%"
                            placeholder="Enter product name"
                            value={product.productName}
                            onChange={(e) =>
                              urgentSourcingStore.updateRaisedReqProductField(
                                productIndex,
                                "productName",
                                e.target.value,
                              )
                            }
                          />
                        </Box>

                        <Box sx={{ flex: 1, mb: 1 }}>
                          <CustomTypography
                            variant="heading6"
                            style={{ fontWeight: 600, marginBottom: 16 }}
                          >
                            Priority{" "}
                            <span
                              style={{
                                color: theme.palette.error.main,
                                marginLeft: 4,
                              }}
                            >
                              *
                            </span>
                          </CustomTypography>
                          <AppDropDown
                            title=""
                            placeholderText=" "
                            fontSize="0.7rem"
                            isMultiSelect={false}
                            value={product.priority?.toString() || "-1"}
                            list={Urgent_sorcing_priority_list.map(
                              (status) => ({
                                id: status.id.toString(),
                                value: status.value,
                              }),
                            )}
                            menuZIndex={10000}
                            onSingleSelectionChange={(id, name) =>
                              urgentSourcingStore.updateRaisedReqProductField(
                                productIndex,
                                "priority",
                                id,
                              )
                            }
                          />
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          mb: 1,
                          display: "flex",
                          gap: 4,
                          flexDirection: { xs: "column", sm: "row" },
                        }}
                      >
                        <Box sx={{ flex: 1, mb: 3 }}>
                          <CustomTypography
                            variant="heading6"
                            style={{ fontWeight: 600, marginBottom: 16 }}
                          >
                            Timeline (IN DAYS){" "}
                            <span
                              style={{
                                color: theme.palette.error.main,
                                marginLeft: 4,
                              }}
                            >
                              *
                            </span>
                          </CustomTypography>
                          <CustomTextField
                            width="100%"
                            placeholder="Enter timeline in days"
                            type="number"
                            allowNegativeValue={false}
                            value={product.timeline || ""}
                            onChange={(e) =>
                              urgentSourcingStore.updateRaisedReqProductField(
                                productIndex,
                                "timeline",
                                e.target.value === ""
                                  ? null
                                  : Number(e.target.value),
                              )
                            }
                          />
                        </Box>

                        <Box sx={{ flex: 1, mb: 3 }}>
                          <CustomTypography
                            variant="heading6"
                            style={{ fontWeight: 600, marginBottom: 16 }}
                          >
                            Quantity{" "}
                            <span
                              style={{
                                color: theme.palette.error.main,
                                marginLeft: 4,
                              }}
                            >
                              *
                            </span>
                          </CustomTypography>
                          <CustomTextField
                            width="100%"
                            allowNegativeValue={false}
                            placeholder="Enter quantity needed"
                            type="number"
                            value={product.quantity || ""}
                            onChange={(e) =>
                              urgentSourcingStore.updateRaisedReqProductField(
                                productIndex,
                                "quantity",
                                e.target.value === ""
                                  ? null
                                  : Number(e.target.value),
                              )
                            }
                          />
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          mb: 1,
                          display: "flex",
                          gap: 4,
                          flexDirection: { xs: "column", sm: "row" },
                        }}
                      >
                        <Box sx={{ flex: 1, mb: 3 }}>
                          <CustomTypography
                            variant="heading6"
                            style={{ fontWeight: 600, marginBottom: 16 }}
                          >
                            Min Price Range{" "}
                            <span
                              style={{
                                color: theme.palette.error.main,
                                marginLeft: 4,
                              }}
                            >
                              *
                            </span>
                          </CustomTypography>
                          <CustomTextField
                            width="100%"
                            placeholder="Enter minimum price range"
                            type="number"
                            allowNegativeValue={false}
                            value={product.minPriceRange || ""}
                            onChange={(e) =>
                              urgentSourcingStore.updateRaisedReqProductField(
                                productIndex,
                                "minPriceRange",
                                e.target.value === ""
                                  ? null
                                  : Number(e.target.value),
                              )
                            }
                          />
                        </Box>

                        <Box sx={{ flex: 1, mb: 3 }}>
                          <CustomTypography
                            variant="heading6"
                            style={{ fontWeight: 600, marginBottom: 16 }}
                          >
                            Max Price Range{" "}
                            <span
                              style={{
                                color: theme.palette.error.main,
                                marginLeft: 4,
                              }}
                            >
                              *
                            </span>
                          </CustomTypography>
                          <CustomTextField
                            width="100%"
                            placeholder="Enter maximum price range"
                            type="number"
                            allowNegativeValue={false}
                            value={product.maxPriceRange || ""}
                            onChange={(e) =>
                              urgentSourcingStore.updateRaisedReqProductField(
                                productIndex,
                                "maxPriceRange",
                                e.target.value === ""
                                  ? null
                                  : Number(e.target.value),
                              )
                            }
                          />
                        </Box>
                      </Box>

                      <Box sx={{ flex: 1, mb: 3 }}>
                        <CustomTypography
                          variant="heading6"
                          style={{ fontWeight: 600, marginBottom: 16 }}
                        >
                          Description{" "}
                          <span
                            style={{
                              color: theme.palette.error.main,
                              marginLeft: 4,
                            }}
                          >
                            *
                          </span>
                        </CustomTypography>
                        <FormSmallTextField
                          id={String(productIndex)}
                          placeholder="Enter detailed product description"
                          value={
                            urgentSourcingStore.raisedRequestFormData.products[
                              productIndex
                            ].description || ""
                          }
                          multiline
                          rows={3}
                          onChange={(id, value) =>
                            urgentSourcingStore.updateRaisedReqProductField(
                              productIndex,
                              "description",
                              value || "",
                            )
                          }
                        />
                      </Box>

                      <Box sx={{ mb: 3 }}>
                        <CustomTypography
                          variant="heading6"
                          style={{ fontWeight: 600, marginBottom: 16 }}
                        >
                          Product Link or Image{" "}
                          <span
                            style={{
                              color: theme.palette.error.main,
                              marginLeft: 4,
                            }}
                          >
                            *
                          </span>
                        </CustomTypography>
                        <CustomTypography
                          variant="caption"
                          style={{
                            color: theme.palette.customColors.greyTextColor,
                            marginBottom: 16,
                            display: "block",
                          }}
                        >
                          Provide either a product link URL or upload a product
                          image
                        </CustomTypography>

                        {/* Link Input Field */}
                        <Box sx={{ mb: 2 }}>
                          <CustomTextField
                            width="100%"
                            placeholder="Enter product link or reference URL"
                            value={product.link}
                            disabled={!!product.imageUrls}
                            onChange={(e) => {
                              urgentSourcingStore.updateRaisedReqProductField(
                                productIndex,
                                "link",
                                e.target.value,
                              );
                            }}
                          />
                          {product.imageUrls && (
                            <CustomTypography
                              variant="caption"
                              style={{
                                color: theme.palette.customColors.greyTextColor,
                                marginTop: 8,
                                display: "block",
                              }}
                            >
                              Remove the image to enable link input
                            </CustomTypography>
                          )}
                        </Box>
                      </Box>

                      <Box>
                        <MediaAssetsUpload
                          key={`product-${productIndex}-images-${
                            urgentSourcingStore.imageUploadKey
                          }-${product.imageUrls ? "has-file" : "no-file"}`}
                          maxWidth={"100%"}
                          uploadText="Upload Product Image"
                          fileInfo="PNG, JPG up to 2MB"
                          multiple={false}
                          existingImageList={[]}
                          newImageList={
                            product.imageUrls ? [product.imageUrls as File] : []
                          }
                          deleteImage={() => {
                            urgentSourcingStore.updateRaisedReqProductField(
                              productIndex,
                              "imageUrls",
                              null,
                            );
                            urgentSourcingStore.incrementImageUploadKey();
                          }}
                          removeImage={(image: {
                            id: string | number;
                            title?: string;
                            img: string;
                          }) => {
                            urgentSourcingStore.updateRaisedReqProductField(
                              productIndex,
                              "imageUrls",
                              null,
                            );
                            urgentSourcingStore.incrementImageUploadKey();
                          }}
                          addImagesToBeUploaded={(files: File[]) => {
                            if (files.length > 0) {
                              urgentSourcingStore.updateRaisedReqProductField(
                                productIndex,
                                "link",
                                "",
                              );
                              urgentSourcingStore.updateRaisedReqProductField(
                                productIndex,
                                "imageUrls",
                                files[0] as File,
                              );
                              urgentSourcingStore.incrementImageUploadKey();
                            }
                          }}
                        />
                        {product.link && product.link.trim() !== "" && (
                          <CustomTypography
                            variant="caption"
                            style={{
                              color: theme.palette.customColors.greyTextColor,
                              marginTop: 8,
                              display: "block",
                            }}
                          >
                            Clear the link field to enable image upload
                          </CustomTypography>
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                );
              },
            )}
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            padding: { xs: "10px 16px 10px", sm: "16px 20px 20px" },
            borderTop: `1px solid ${theme.palette.customColors.greyBorder}`,
            backgroundColor: theme.palette.customColors.lightGreyColor,
            justifyContent: "flex-end",
            gap: 1,
          }}
        >
          <AppButton
            label="Reset"
            onClick={handleReset}
            variant="grayscale"
            customStyles={{
              borderColor: theme.palette.customColors.greyDarkborder,
              color: theme.palette.customColors.greyTextColor,
              fontWeight: 600,
              padding: "8px 20px",
              borderRadius: "8px",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                transform: "translateY(-1px)",
              },
            }}
          />

          <AppButton
            onClick={onClose}
            variant="grayscale"
            label="Cancel"
            customStyles={{
              borderColor: theme.palette.customColors.greyDarkborder,
              color: theme.palette.customColors.greyTextColor,
              fontWeight: 600,
              padding: "8px 20px",
              borderRadius: "8px",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                transform: "translateY(-1px)",
              },
            }}
          />

          <AppButton
            onClick={handleSave}
            variant="primary"
            label="Save"
            disabled={!isFormValid()}
            customStyles={{
              backgroundColor: isFormValid()
                ? theme.palette.customColors.EmeraldGreen
                : theme.palette.customColors.greyDarkborder,
              color: theme.palette.customColors.white,
              fontWeight: 600,
              padding: "8px 20px",
              borderRadius: "8px",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                backgroundColor: isFormValid()
                  ? theme.palette.customColors.greenDark
                  : theme.palette.customColors.greyDarkborder,
                transform: isFormValid() ? "translateY(-1px)" : "none",
                boxShadow: isFormValid()
                  ? `0 4px 12px ${theme.palette.customColors.EmeraldGreen}30`
                  : "none",
              },
              "&:disabled": {
                backgroundColor: theme.palette.customColors.greyDarkborder,
                color: theme.palette.customColors.white,
                cursor: "not-allowed",
              },
            }}
          />
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default observer(UrgentSourcingRaiseRequestPopUp);
